import React, { useState, useEffect } from "react";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
} from "lucide-react";
import { businessLogin } from "../ApiCalls/authentication";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { connect } from "react-redux";
import {
  userLogin,
  setUser,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "../Redux/modules/auth";
import { sendRecoverPasswordEmail } from "../ApiCalls/mantisBusiness";
import {
  DEFAULT_AFTER_LOGIN,
  REDIRECT_PARAM,
  safeRedirectPath,
} from "../utils/authRedirect";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
  };
};

function BusinessLogin(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [showSessionExpiredToast, setShowSessionExpiredToast] = useState(false);
  const [showLoginRequiredToast, setShowLoginRequiredToast] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [loginRequiredToastVisible, setLoginRequiredToastVisible] =
    useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  // Set by ProtectedRoute and by fetchWrapper's 401: the page the person was on.
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirectPath(searchParams.get(REDIRECT_PARAM));

  function showToast() {
    setShowSessionExpiredToast(true);
    setToastVisible(true);

    setTimeout(() => {
      setToastVisible(false);
      setTimeout(() => {
        setShowSessionExpiredToast(false);
      }, 500);
    }, 3000);
  }

  function handleShowLoginRequiredToast() {
    setShowLoginRequiredToast(true);
    setLoginRequiredToastVisible(true);

    setTimeout(() => {
      setLoginRequiredToastVisible(false);
      setTimeout(() => {
        setShowLoginRequiredToast(false);
      }, 500);
    }, 4000);
  }

  useEffect(() => {
    const sessionExpired = localStorage.getItem("sessionExpired");

    if (sessionExpired) {
      showToast();
      localStorage.removeItem("sessionExpired");
    } else if (redirectTo) {
      handleShowLoginRequiredToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitLogin(event) {
    if (!loggingIn) {
      event.preventDefault();
      event.stopPropagation();
      setLoggingIn(true);
      const loginRes = await businessLogin(email, password);
      if (loginRes.success) {
        // L-01 (MA-06): the backend now sets the mantis_business_session (httpOnly)
        // and businessCsrfToken cookies itself via Set-Cookie — nothing to persist here.
        await updateUserSession(
          loginRes.userId,
          loginRes.user,
          loginRes.token,
          loginRes.mantisBusiness,
        );
      } else {
        alert("Credenciales incorrectas");
      }
      setLoggingIn(false);
    }
  }

  function openForgotPasswordModal() {
    setForgotPasswordEmail("");
    setForgotPasswordLoading(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError("");
    setShowForgotPasswordModal(true);
  }

  function closeForgotPasswordModal() {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail("");
    setForgotPasswordLoading(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError("");
  }

  async function handleSendRecoverPasswordMail(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!forgotPasswordEmail || forgotPasswordLoading) return;
    setForgotPasswordLoading(true);
    setForgotPasswordError("");

    try {
      const res = await sendRecoverPasswordEmail(
        forgotPasswordEmail.trim().toLowerCase(),
      );
      if (res && res.success) {
        setForgotPasswordSuccess(true);
      } else {
        setForgotPasswordError(
          res?.message || "No pudimos procesar la solicitud. Intenta de nuevo.",
        );
      }
    } catch (err) {
      console.error("Error al enviar correo de recuperación:", err);
      setForgotPasswordError(
        "Error de conexión al servidor. Inténtalo más tarde.",
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  }

  function updateUserSession(userId, user, token, mantisBusiness) {
    props.setUser({ user });
    props.setToken({ token });
    props.setUserId({ userId });
    props.setMantisBusiness({ mantisBusiness });
    props.userLogin();
    props.setIsBusiness({ isBusiness: true });

    // Back to the page that asked for the sign-in, replacing the login in the history.
    navigate(redirectTo || DEFAULT_AFTER_LOGIN, { replace: true });
  }

  // B-86 — the Cierres redesign's look (Red Hat Display + Lora, its neutrals, borders, cards and
  // its dark primary button) with Mantis's own logo. Only what is drawn changed: every handler
  // above — `submitLogin`, the recovery email, the `?redirect=` B-75 follows — is as it was.
  const campo =
    "h-10 w-full rounded-[8px] border bg-card pl-9 pr-3 text-[13px] text-brinks-foreground outline-none transition placeholder:text-brinks-muted-foreground/70 focus:border-brinks-border-strong focus:ring-2 focus:ring-brinks-ring/10 disabled:opacity-60";
  const etiqueta = "block text-[12px] font-medium text-brinks-muted-foreground";
  const botonPrincipal =
    "inline-flex h-10 w-full items-center justify-center gap-2 rounded-[8px] bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50";
  const botonSecundario =
    "inline-flex h-10 w-full items-center justify-center rounded-[8px] border bg-card px-4 text-[13px] font-medium text-brinks-foreground transition hover:bg-brinks-surface-hover disabled:opacity-50";
  const enlace =
    "text-[12.5px] font-medium text-brinks-foreground underline-offset-4 transition hover:underline disabled:opacity-50";

  return (
    <AuthLayout
      titulo="Inteligencia artificial que trabaja con tu equipo"
      subtitulo="Agentes, flujos y documentos automatizados para que tu empresa opere más rápido y con menos errores."
    >
      <section className="rounded-[14px] border bg-card p-6 shadow-brinks-card sm:p-7">
        <header className="mb-5">
          <h1 className="font-lora text-[22px] font-semibold text-brinks-foreground">
            Iniciar sesión
          </h1>
          <p className="mt-1 text-[13px] text-brinks-muted-foreground">
            Ingresa tus credenciales para acceder a tu cuenta.
          </p>
        </header>

        <form onSubmit={submitLogin} className="grid gap-4">
          <div className="grid gap-1.5">
            <label htmlFor="login-correo" className={etiqueta}>
              Correo electrónico
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
              <input
                id="login-correo"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                className={campo}
                disabled={loggingIn}
                required
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="login-contrasena" className={etiqueta}>
                Contraseña
              </label>
              <button
                type="button"
                onClick={openForgotPasswordModal}
                className={enlace}
                disabled={loggingIn}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
              <input
                id="login-contrasena"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={`${campo} pr-10`}
                disabled={loggingIn}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-[6px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover hover:text-brinks-foreground"
                disabled={loggingIn}
                aria-label="Alternar visibilidad de contraseña"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loggingIn || !email || !password}
            className={`${botonPrincipal} mt-1`}
          >
            {loggingIn ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Ingresar <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </section>

      <p className="mt-5 text-center text-[12.5px] text-brinks-muted-foreground">
        ¿No tienes una cuenta?{" "}
        <button
          type="button"
          onClick={() => navigate("/businessRegister")}
          className={enlace}
          disabled={loggingIn}
        >
          Regístrate aquí
        </button>
      </p>

      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="recuperar-titulo"
            className="relative w-full max-w-[400px] rounded-[14px] border bg-card p-6 shadow-lg"
          >
            <button
              type="button"
              onClick={closeForgotPasswordModal}
              disabled={forgotPasswordLoading}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-[8px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover hover:text-brinks-foreground"
              aria-label="Cerrar modal"
            >
              <X className="h-4 w-4" />
            </button>

            {!forgotPasswordSuccess ? (
              <div className="grid gap-4">
                <header className="pr-8">
                  <h2
                    id="recuperar-titulo"
                    className="font-lora text-[19px] font-semibold text-brinks-foreground"
                  >
                    Recuperar contraseña
                  </h2>
                  <p className="mt-1 text-[13px] text-brinks-muted-foreground">
                    Ingresa tu correo y te enviaremos un enlace seguro para
                    restablecer tu contraseña.
                  </p>
                </header>

                {forgotPasswordError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-3 py-2 text-[12.5px] text-brinks-danger"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-semibold">
                        No se pudo enviar el enlace
                      </p>
                      <p>{forgotPasswordError}</p>
                    </div>
                  </div>
                )}

                <form
                  onSubmit={handleSendRecoverPasswordMail}
                  className="grid gap-4"
                >
                  <div className="grid gap-1.5">
                    <label htmlFor="recuperar-correo" className={etiqueta}>
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brinks-muted-foreground" />
                      <input
                        id="recuperar-correo"
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        placeholder="tu.nombre@empresa.com"
                        className={campo}
                        required
                        disabled={forgotPasswordLoading}
                        autoFocus
                      />
                    </div>
                    <p className="flex items-center gap-1.5 text-[11.5px] text-brinks-muted-foreground">
                      <Lock className="h-3.5 w-3.5 shrink-0" /> El enlace expira
                      en 60 minutos por políticas de seguridad.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={closeForgotPasswordModal}
                      disabled={forgotPasswordLoading}
                      className={botonSecundario}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading || !forgotPasswordEmail}
                      className={botonPrincipal}
                    >
                      {forgotPasswordLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
                        </>
                      ) : (
                        "Enviar enlace"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="grid gap-4 text-center">
                <span className="mx-auto inline-flex items-center gap-1.5 rounded-full border border-brinks-success-border bg-brinks-success-soft px-2.5 py-0.5 text-[11.5px] font-semibold text-brinks-success">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Enlace enviado
                </span>
                <div>
                  <h2
                    id="recuperar-titulo"
                    className="font-lora text-[19px] font-semibold text-brinks-foreground"
                  >
                    Revisa tu correo
                  </h2>
                  <p className="mt-1 text-[13px] text-brinks-muted-foreground">
                    Enviamos un enlace para restablecer tu contraseña a:
                  </p>
                  <p className="mt-2 inline-block rounded-[6px] border bg-brinks-surface px-2.5 py-1 text-[13px] font-medium text-brinks-foreground">
                    {forgotPasswordEmail}
                  </p>
                </div>
                <p className="text-[11.5px] text-brinks-muted-foreground">
                  Si no lo encuentras en unos minutos, revisa tu carpeta de spam
                  o correo no deseado.
                </p>
                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    className={botonPrincipal}
                  >
                    Volver a iniciar sesión
                  </button>
                  <button
                    type="button"
                    onClick={handleSendRecoverPasswordMail}
                    disabled={forgotPasswordLoading}
                    className={enlace}
                  >
                    {forgotPasswordLoading
                      ? "Reenviando…"
                      : "¿No recibiste el correo? Reenviar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showSessionExpiredToast && (
        <div
          role="status"
          className={`fixed right-4 top-4 z-50 max-w-sm rounded-[10px] border border-brinks-danger-border bg-card p-3.5 shadow-lg transition-all duration-300 ${
            toastVisible
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          }`}
        >
          <div className="flex items-start gap-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brinks-danger" />
            <div>
              <p className="text-[13px] font-semibold text-brinks-foreground">
                Sesión Expirada
              </p>
              <p className="text-[12.5px] text-brinks-muted-foreground">
                Tu sesión ha expirado. Por favor, inicia sesión nuevamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {showLoginRequiredToast && (
        <div
          role="status"
          className={`fixed right-4 top-4 z-50 max-w-sm rounded-[10px] border bg-card p-3.5 shadow-lg transition-all duration-300 ${
            loginRequiredToastVisible
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          }`}
        >
          <div className="flex items-start gap-2.5">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-brinks-foreground" />
            <div>
              <p className="text-[13px] font-semibold text-brinks-foreground">
                Login Requerido
              </p>
              <p className="text-[12.5px] text-brinks-muted-foreground">
                Necesitas iniciar sesión para acceder a esa página. Después del
                login te llevaremos allí automáticamente.
              </p>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}

export default connect(select, mapDispatchToProps)(BusinessLogin);
